import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  findingsService,
  type BulkTriageResult,
  type FalsePositiveReason,
} from '@services/findingsService';
import type { Finding } from '@services/auditService';
import type { FindingsTableSelection } from '@components/findings/FindingsTable';
import { toDisplayError } from '@utils/apiError';

/**
 * REV-220/REV-509, extracted for REV-720: selecting findings and dispositioning
 * them in bulk.
 *
 * This lived inside OrgFindingsPage, which is why the scan-run view had no way
 * to offer it — the ticket asks for the two to have identical features, and a
 * feature welded to one page cannot be shared. The state and the rules live
 * here; `BulkTriageBar` renders them; `FindingsTable` takes the `selection`.
 *
 * Two rules are worth stating rather than leaving to the code:
 *
 *   - Bulk triage acts on automated finding INSTANCES, by their bare `raw_id`.
 *     A manual finding is a `TestEvaluation` inside an audit session and has no
 *     instance to disposition, so it is never selectable.
 *   - Without an organization there is nothing to act in, and `selection` is
 *     undefined rather than inert. The table then renders no checkbox column at
 *     all, which is how "you cannot triage here" is said — instead of
 *     checkboxes that tick and then refuse.
 */

/** REV-220: acts on automated instances, by their bare finding-instance id. */
export const isSelectable = (f: Finding) => f.source === 'auto' && !!f.raw_id;

/**
 * REV-220: reasons accepted by the bulk false-positive endpoint.
 * REV-509: won't-fix causes ("by_design", "third_party", …) are NOT valid here —
 * a false positive asserts the finding is WRONG, while won't fix concedes it is
 * right and declines to act. Mirrors src/constants/dispositionReasons.js in
 * revenant-api, which rejects the rest.
 */
export const FALSE_POSITIVE_REASONS: FalsePositiveReason[] = [
  'incorrect_detection',
  'incorrect_wcag_mapping',
  'context_specific',
  'testing_artifact',
  'other',
];

export interface UseBulkTriageInput {
  organizationId: string | null | undefined;
  /**
   * REV-949: whether the caller's role may change a finding's status
   * (canTriageFindings). A viewer may not, and the API refuses them, so the
   * view offers no selection. Required, so every caller has to decide.
   */
  canTriage: boolean;
  /** The rows currently on screen. Select-all means "all of these". */
  rows: Finding[];
  /** Called after a successful action, to reload the list. */
  onChanged: () => void | Promise<void>;
  /** Where a CONFIRM failure is surfaced — the caller's page-level error. */
  onError: (message: string) => void;
}

export interface BulkTriage {
  /** Undefined when triage is impossible; the table then has no checkboxes. */
  selection: FindingsTableSelection | undefined;
  selectedCount: number;
  submitting: boolean;
  toast: string | null;
  dismissToast: () => void;
  clear: () => void;
  confirm: () => Promise<void>;
  fpOpen: boolean;
  openFp: () => void;
  closeFp: () => void;
  fpReason: FalsePositiveReason;
  setFpReason: (r: FalsePositiveReason) => void;
  fpNotes: string;
  setFpNotes: (n: string) => void;
  fpError: string | null;
  markFalsePositive: () => Promise<void>;
}

export function useBulkTriage({
  organizationId,
  canTriage,
  rows,
  onChanged,
  onError,
}: UseBulkTriageInput): BulkTriage {
  const { t } = useTranslation();

  // Holds raw_ids (finding-instance ids), not the namespaced `id` the list
  // endpoint returns — the bulk endpoints take the bare uuid.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fpOpen, setFpOpen] = useState(false);
  const [fpReason, setFpReason] = useState<FalsePositiveReason>('incorrect_detection');
  const [fpNotes, setFpNotes] = useState('');
  const [fpError, setFpError] = useState<string | null>(null);

  const selectableRows = useMemo(() => rows.filter(isSelectable), [rows]);
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  /**
   * Drop selected ids that are no longer on screen — a new page, a changed
   * filter, a reload after acting. Acting on a row the user can no longer see
   * is the bug this prevents.
   *
   * Two things keep it safe for a caller whose `rows` array is rebuilt on every
   * render (ScanRunFindings derives its with `.slice()`), and it is worth being
   * precise about which does the work:
   *
   *   - It PRUNES rather than clears, so running it again with the same ids is
   *     a no-op. This is what actually makes repeat renders harmless.
   *   - It is keyed on the ids rather than the array, so it mostly does not run
   *     again at all. That is churn reduction, not correctness — I assumed it
   *     was correctness until a mutation showed the identity-keyed version
   *     passing every test.
   *
   * Either alone is enough. Replacing the prune with a clear AND keying on the
   * array is the regression, and `survives a caller that builds a new rows
   * array on every render` is the test that catches it.
   */
  const selectableKey = selectableRows.map((f) => f.raw_id).join(',');
  useEffect(() => {
    const present = new Set(selectableKey ? selectableKey.split(',') : []);
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableKey]);

  /**
   * REV-951: a caller that loses the role to triage loses its selection too.
   * The checkbox column goes when `canTriage` does, so a selection kept past
   * that would leave the bulk bar on screen offering actions that do nothing,
   * with no way to see or clear what it holds. Cleared, not hidden, so
   * regaining the role does not bring back a selection made before.
   */
  useEffect(() => {
    if (!canTriage) setSelected((prev) => (prev.size === 0 ? prev : new Set()));
  }, [canTriage]);

  const toggleOne = useCallback((rawId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rawId)) next.delete(rawId);
      else next.add(rawId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === selectableRows.length ? new Set() : new Set(selectableRows.map((f) => f.raw_id))
    );
  }, [selectableRows]);

  const clear = useCallback(() => setSelected(new Set()), []);

  /**
   * Undefined — no checkbox column at all — when this view cannot select
   * anything, which callers signal by passing no `organizationId`, or when the
   * caller's role cannot change a finding's status (`canTriage`, REV-949): a
   * checkbox column for a viewer would only lead to a refusal, and a disabled
   * one would say nothing the finding detail page does not explain. An
   * unregistered scan does exactly that: it persists no FindingInstance, so
   * nothing it lists could ever be dispositioned, and a select-all rendered
   * disabled would be a control existing only to be refused.
   *
   * Deliberately NOT inferred from `selectableRows.length`. That was the first
   * attempt and it was wrong: it cannot tell a view that can never select
   * anything from a list that has nothing selectable *yet*. Measured — the
   * checkbox column appeared after load (4 columns, then 5), shifting every
   * other column and changing a table's announced dimensions mid-read. Whether
   * selection is possible is a property of the view, not of the rows currently
   * in it, so the caller says.
   */
  const selection: FindingsTableSelection | undefined =
    organizationId && canTriage
      ? {
          selected,
          isSelectable,
          selectableCount: selectableRows.length,
          allSelected: selectableRows.length > 0 && selected.size === selectableRows.length,
          someSelected: selected.size > 0 && selected.size !== selectableRows.length,
          onToggleAll: toggleAll,
          onToggleOne: toggleOne,
        }
      : undefined;

  /**
   * REV-909: the toast reports what the API did. It skips a finding already
   * confirmed, dispositioned or resolved, and a toast counting the ids SENT
   * claimed those had changed too.
   */
  const outcome = (done: string, { skipped }: BulkTriageResult) =>
    skipped > 0
      ? `${done} ${t('findings.bulk.skipped', {
          defaultValue:
            '{{count}} skipped: already confirmed, marked a false positive or won’t fix, or resolved.',
          count: skipped,
        })}`
      : done;

  const confirm = async () => {
    if (!organizationId || !canTriage || selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      const result = await findingsService.bulkConfirm(organizationId, selectedIds);
      setToast(
        outcome(
          t('findings.bulk.confirmed', {
            defaultValue: '{{count}} finding(s) confirmed.',
            count: result.processed,
          }),
          result
        )
      );
      setSelected(new Set());
      await onChanged();
    } catch (err) {
      onError(
        toDisplayError(
          err,
          t('findings.bulk.confirmFailed', 'Failed to confirm the selected findings')
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const markFalsePositive = async () => {
    if (!organizationId || !canTriage || selectedIds.length === 0) return;

    // REV-509: the claim must be justified in writing. Validated on submit
    // rather than by disabling the button, so the user is told what is missing
    // instead of being left to guess — the never-pre-disable rule.
    const trimmedNotes = fpNotes.trim();
    if (!trimmedNotes) {
      setFpError(
        t(
          'findings.bulk.fpDialog.notesRequired',
          'Notes are required. Explain why these findings are incorrect.'
        )
      );
      return;
    }

    setSubmitting(true);
    setFpError(null);
    try {
      const result = await findingsService.bulkMarkFalsePositive(
        organizationId,
        selectedIds,
        fpReason,
        trimmedNotes
      );
      setFpOpen(false);
      setFpNotes('');
      setToast(
        outcome(
          t('findings.bulk.markedFalsePositive', {
            defaultValue: '{{count}} finding(s) marked as false positive.',
            count: result.processed,
          }),
          result
        )
      );
      setSelected(new Set());
      await onChanged();
    } catch (err) {
      // Stays inside the dialog, which stays open, carrying the server's own
      // message — Axios's "Request failed with status code 400" tells the user
      // nothing actionable (REV-509). A page-level error behind a closed dialog
      // is a failure nobody sees.
      setFpError(
        toDisplayError(err, t('findings.bulk.fpFailed', 'Failed to mark the selected findings'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    selection,
    selectedCount: selected.size,
    submitting,
    toast,
    dismissToast: () => setToast(null),
    clear,
    confirm,
    fpOpen,
    openFp: () => setFpOpen(true),
    closeFp: () => {
      setFpOpen(false);
      // The notes stay — losing someone's typing is worse than anything this
      // fixes. The error does not: it describes a request that is no longer
      // being made, and reopening the dialog to be told a submission failed
      // before submitting anything is a lie about the current state. Behaviour
      // inherited from OrgFindingsPage, which never cleared it either.
      setFpError(null);
    },
    fpReason,
    setFpReason,
    fpNotes,
    setFpNotes: (n: string) => {
      setFpNotes(n);
      setFpError((prev) => (prev ? null : prev));
    },
    fpError,
    markFalsePositive,
  };
}
